import React from 'react';
import Text from '@/components/Text';
import { SlideProps } from './slideTypes';
import { PIE_CHART_COLORS, SLIDE_COMPONENT_TEXT_SIZE, SLIDE_COMPONENT_TITLE_SIZE } from './slideConstants';

export function PieChartSlide({ slide }: SlideProps) {
  const chartData = Array.isArray(slide.data?.chartData) ? slide.data.chartData : [];
  const chartTitle = slide.data?.title || 'Distribution';

  const validChartData = chartData.filter(
    (item: any) => item && (typeof item.value === 'number' || !isNaN(Number(item.value))) && item.label
  );

  if (validChartData.length === 0) return null;

  const total = validChartData.reduce((sum: number, item: any) => sum + Number(item.value), 0);

  // Pie chart calculations
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  let currentAngle = -90; // Start from top

  const segments = validChartData.map((item: any, idx: number) => {
    const value = Number(item.value);
    const percentage = (value / total) * 100;
    const angle = (percentage / 100) * 360;
    
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;
    const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return {
      pathData,
      color: PIE_CHART_COLORS[idx % PIE_CHART_COLORS.length],
      label: item.label,
      percentage: percentage.toFixed(1),
    };
  });

  return (
    <div className="flex flex-col gap-4 md:gap-6 w-full px-4 md:px-6 py-6 md:py-8 items-start justify-center overflow-y-auto max-h-full">
      <Text size={SLIDE_COMPONENT_TITLE_SIZE} className="text-white font-normal mb-1 md:mb-2 text-left text-sm md:text-lg">{chartTitle}</Text>
      
      <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-12 backdrop-blur-sm">
        {/* SVG Pie */}
        <div className="relative w-40 h-40 md:w-64 md:h-48 flex items-center justify-center flex-none">
          <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-0">
            {segments.map((segment, idx) => (
              <path
                key={idx}
                d={segment.pathData}
                fill={segment.color}
                className="transition-all duration-1000 ease-out hover:opacity-80"
                style={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
              />
            ))}
            {/* Center cutout for donut effect if desired, or just leave as pie */}
            <circle cx="100" cy="100" r="40" fill="rgba(0,0,0,0.2)" />
          </svg>
                    </div>

        {/* Legend */}
        <div className="flex-1 w-full flex flex-col gap-2 md:gap-4">
          {segments.map((segment, idx) => (
            <div key={idx} className="flex items-center justify-between group">
              <div className="flex items-center gap-2 md:gap-3">
                    <div 
                  className="w-3 h-3 md:w-4 md:h-4 rounded-full border border-white/20 shadow-sm flex-none" 
                  style={{ backgroundColor: segment.color }} 
                />
                <Text size={SLIDE_COMPONENT_TEXT_SIZE} className="text-white font-normal text-xs md:text-md line-clamp-1">{segment.label}</Text>
                  </div>
              <div className="flex flex-col items-end">
                <Text size={SLIDE_COMPONENT_TEXT_SIZE} className="text-white font-normal text-xs md:text-md">{segment.percentage}%</Text>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
